import { useMemo, useState } from 'react';

import { Flag02Icon } from '@hugeicons-pro/core-solid-rounded';
import {
  ComputerUserIcon,
  Delete02Icon,
  DownloadSquare01Icon,
  Link01Icon,
  QuestionIcon,
  SentIcon,
  User02Icon,
  UserSwitchIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import moment from 'moment';
import { Tab, Tabs } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { IconComponent } from 'components/Icon/IconComponent';
import LinkShare from 'components/LinkShare/LinkShare';
import { SummaryStat } from 'components/SummaryStat/SummaryStat';

import { useGetReferenceCandidateResponseQuery } from 'api/candidates-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { useGetRefereeResponseQuery } from 'api/referees-api';
import { selectUser } from 'api/user-slice';

import { CandidateAnswers } from '../CandidateAnswers/CandidateAnswers';
import { RefereeMetadataDetailsModal } from '../RefereeMetadataDetailsModal/RefereeMetadataDetailsModal';
import { ReplaceRefereeModal } from '../ReplaceRefereeModal/ReplaceRefereeModal';
import { RefereeDetails } from './../RefereeDetails/RefereeDetails';
import { RefereeReport } from './../RefereeReport/RefereeReport';

export const Referee = ({
  brand = {},
  candidate,
  check,
  company,
  referee,
  canAccessResults = true,
  messagePopUp = () => {},
  updateReferee = () => {},
  deleteReferee = () => {},
  cancelReferee = () => {},
  setLoading = () => {},
  getPdfReport = () => {},
  setShowRequestAlert = () => {},
  onNewRequest = () => {},
  sendReplacementRequest = () => {},
  onClose = () => {},
}) => {
  const [tab, setTab] = useState();

  const [showDeletionAlert, setShowDeletionAlert] = useState(false);
  const [showMetadataDetails, setShowMetadaDetails] = useState(false);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const user = useSelector(selectUser);
  const answered_on_behalf_of = useMemo(() => user.id, [user]);
  const companyChecks = useSelector(selectCompanyChecks);

  const { data: candidateResponse } = useGetReferenceCandidateResponseQuery({
    id: candidate.id,
    refereeId: referee.id,
  });
  const { data: refereeResponse } = useGetRefereeResponseQuery(referee.id, {
    skip: !referee.completed_at,
  });

  const summaryStats = useMemo(() => {
    const candidateFlags = candidateResponse?.flag_severities || {};
    const refereeFlags = refereeResponse?.flag_severities || {};
    const candidateSeverities = Object.values(candidateFlags);
    const refereeSeverities = Object.values(refereeFlags);
    const bySeverity = severities =>
      severities.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
    const candidateBySeverity = bySeverity(candidateSeverities);
    const refereeBySeverity = bySeverity(refereeSeverities);
    return {
      candidateFormName: candidateResponse?.form?.name ?? '—',
      refereeFormName: refereeResponse?.form?.name ?? referee?.form_name ?? '—',
      candidateSubmittedAt: check?.submitted_at ?? null,
      refereeSubmittedAt: referee?.submitted_at ?? null,
      candidate: {
        totalFlagged: candidateSeverities.length,
        high: candidateBySeverity.high ?? 0,
        medium: candidateBySeverity.medium ?? 0,
        low: candidateBySeverity.low ?? 0,
      },
      referee: {
        totalFlagged: refereeSeverities.length,
        high: refereeBySeverity.high ?? 0,
        medium: refereeBySeverity.medium ?? 0,
        low: refereeBySeverity.low ?? 0,
      },
    };
  }, [
    candidateResponse,
    refereeResponse,
    referee?.form_name,
    referee?.submitted_at,
    check?.submitted_at,
  ]);

  const handleUpdate = async data => {
    setLoading(true);
    await updateReferee(data.id, data);
    setLoading(false);
    messagePopUp('Referee updated', MESSAGE_STATE_SUCCESS);
    if (!referee.completed_at) setShowRequestAlert(true);
  };

  const handleDownloadReport = () => {
    const filename = `${candidate.first_name}_${candidate.last_name}-${referee.first_name}_${referee.last_name}.pdf`;
    getPdfReport(referee.id, filename);
  };

  const handleDeletion = () => {
    setShowDeletionAlert(false);
    onClose();
    deleteReferee(referee.id);
  };

  const handleRefereeCancelation = () => {
    setShowReplacementModal(false);
    onClose();
    cancelReferee(referee.id);
  };

  const handleReplacementRequest = async (templateId, message) => {
    setShowReplacementModal(false);
    await sendReplacementRequest(referee.id, templateId, message);
  };

  const formUrl = useMemo(() => {
    const companyCheck = companyChecks.find(cc => cc.type === check.type);
    return companyCheck?.configuration?.use_new_form
      ? `${process.env.REACT_APP_WEB_URL}/form_submission/new_referee/`
      : `${process.env.REACT_APP_WEB_URL}/form_submission/referee/`;
  }, [companyChecks]);

  const iconConfigs = useMemo(
    () => [
      {
        icon: QuestionIcon,
        tip: 'Answer on behalf',
        onClick: () => {
          window.open(`${formUrl}${referee.token}/${answered_on_behalf_of}`, '_blank');
        },
        disabled: referee.completed_at,
      },
      {
        icon: Link01Icon,
        tip: 'Link',
        onClick: () => setShowLink(true),
        disabled: !referee.completed_at,
      },
      {
        icon: UserSwitchIcon,
        tip: 'Ask candidate to replace referee',
        onClick: () => setShowReplacementModal(true),
      },
      {
        icon: SentIcon,
        tip: 'Send New Request to Referee',
        onClick: onNewRequest,
        disabled: referee.completed_at,
      },
      {
        icon: ComputerUserIcon,
        tip: 'Metadata details',
        onClick: () => setShowMetadaDetails(true),
      },
      {
        icon: DownloadSquare01Icon,
        tip: 'Download Form',
        onClick: handleDownloadReport,
        disabled: !referee.completed_at || !canAccessResults,
      },
      {
        icon: Delete02Icon,
        type: 'error',
        tip: 'Delete',
        onClick: () => setShowDeletionAlert(true),
      },
    ],
    [referee.token, referee.completed_at, answered_on_behalf_of],
  );

  return (
    <div className="bg-neutral-1 rounded-3xl border border-neutral-4 shadow-sm w-1/2 overflow-hidden">
      <Alert
        show={showDeletionAlert}
        title={`Are you sure you want to delete ${referee?.first_name}?`}
        message="Referee deletion cannot be undone. Once deleted, we will adjust the number of referees for this candidate"
        ok="Delete"
        type="error"
        onOk={handleDeletion}
        icon={Delete02Icon}
        showConfirmationInput
        cancel="Cancel"
        onCancel={() => setShowDeletionAlert(false)}
      />
      <RefereeMetadataDetailsModal
        candidate={candidate}
        referee={referee}
        referenceCheck={check}
        visible={showMetadataDetails}
        onClose={() => setShowMetadaDetails(false)}
      />
      <ReplaceRefereeModal
        brand={brand}
        referee={referee}
        visible={showReplacementModal}
        onClose={() => setShowReplacementModal(false)}
        onCancel={handleRefereeCancelation}
        onReplacementRequest={handleReplacementRequest}
      />
      <div className="p-8 flex flex-col gap-8">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <IconComponent icon={User02Icon} />
            <div className="flex flex-col gap-1">
              <p className="text-lg font-medium text-text-tertiary uppercase tracking-wide">
                Referee Name
              </p>
              <h1 className="text-4xl font-medium tracking-tight text-neutral-7">
                {referee?.name}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LinkShare
              open={showLink}
              link={`${process.env.REACT_APP_WEB_URL}/company/report/reference/${referee.token}`}
              title="Share this report"
              onClose={() => setShowLink(false)}
            />
            {iconConfigs.map(({ icon, type = 'secondary', tip, onClick, disabled }, index) => (
              <IconComponent
                key={index}
                icon={icon}
                type={type}
                border={false}
                noBackground
                strokeWidth={1.5}
                size="medium"
                tip={tip}
                onClick={onClick}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6">
          <SummaryStat
            label="Candidate form"
            value={summaryStats.candidateFormName}
            title={summaryStats.candidateFormName}
            truncate
          />
          <SummaryStat
            label="Referee form"
            value={summaryStats.refereeFormName}
            title={summaryStats.refereeFormName}
            truncate
          />
          <SummaryStat
            label="Candidate submitted"
            value={
              summaryStats.candidateSubmittedAt
                ? moment(summaryStats.candidateSubmittedAt).format('MMMM D, YYYY')
                : '—'
            }
          />
          <SummaryStat
            label="Referee submitted"
            value={
              summaryStats.refereeSubmittedAt
                ? moment(summaryStats.refereeSubmittedAt).format('MMMM D, YYYY')
                : '—'
            }
          />
          <SummaryStat
            label="Candidate flagged"
            value={
              summaryStats.candidate.totalFlagged > 0 ? summaryStats.candidate.totalFlagged : '-'
            }
            tooltip="High, medium and low risk flagged response counts (candidate form)"
            {...(() => {
              const items = [
                {
                  count: summaryStats.candidate.high,
                  className: 'text-red-2',
                  tooltip: 'High risk',
                },
                {
                  count: summaryStats.candidate.medium,
                  className: 'text-orange-2',
                  tooltip: 'Medium risk',
                },
                {
                  count: summaryStats.candidate.low,
                  className: 'text-blue-3',
                  tooltip: 'Low risk',
                },
              ].filter(item => item.count > 0);
              return items.length > 0
                ? {
                    breakdown: items,
                    breakdownIcon: <HugeiconsIcon icon={Flag02Icon} size={20} />,
                  }
                : {};
            })()}
          />
          <SummaryStat
            label="Referee flagged"
            value={summaryStats.referee.totalFlagged > 0 ? summaryStats.referee.totalFlagged : '-'}
            tooltip="High, medium and low risk flagged response counts (referee form)"
            {...(() => {
              const items = [
                { count: summaryStats.referee.high, className: 'text-red-2', tooltip: 'High risk' },
                {
                  count: summaryStats.referee.medium,
                  className: 'text-orange-2',
                  tooltip: 'Medium risk',
                },
                { count: summaryStats.referee.low, className: 'text-blue-3', tooltip: 'Low risk' },
              ].filter(item => item.count > 0);
              return items.length > 0
                ? {
                    breakdown: items,
                    breakdownIcon: <HugeiconsIcon icon={Flag02Icon} size={20} />,
                  }
                : {};
            })()}
          />
        </div>
      </div>
      <Tabs
        activeKey={tab}
        defaultActiveKey={1}
        onSelect={setTab}
        className="tabs padding-small no-padding"
        mountOnEnter={true}>
        <Tab eventKey={1} title="Details">
          <RefereeDetails
            referee={referee}
            company={company}
            onSubmit={handleUpdate}
            setShowRequestAlert={setShowRequestAlert}
          />
        </Tab>
        <Tab eventKey={2} title="Candidate" disabled={!candidate.submitted_at && !referee.manual}>
          <CandidateAnswers candidate={candidate} referee={referee} setLoading={setLoading} />
        </Tab>
        {canAccessResults && (
          <Tab eventKey={3} title="Report" disabled={!referee.completed_at}>
            <RefereeReport
              candidate={candidate}
              referee={referee}
              referenceCheck={check}
              setLoading={setLoading}
            />
          </Tab>
        )}
      </Tabs>
    </div>
  );
};
